import React, { FC, useRef } from "react";
import { Download, X } from "lucide-react";
import { Button } from "./ui/button";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "../../api/axios";
import toast from "react-hot-toast";
import { useMessage, useThreadListItem } from "@assistant-ui/react";
import { FILE_TYPE } from "../../types";
import { Artifact } from "../../types/chat-types";
import { validate as isValidUUID } from "uuid";
import debounce from "lodash/debounce";
import { Chart as ChartJS } from "chart.js";
import rehypeRaw from "rehype-raw";
import { applyChartDefaults } from "../../@/components/assistant-ui/chart-utils";
import { loadChart } from "../../@/components/assistant-ui/chartjs-diagram";

interface ArtifactPanelProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  artifact: Artifact;
  role: string;
}

export const ArtifactPanel: FC<ArtifactPanelProps> = ({
  isOpen,
  onClose,
  title,
  artifact,
  role,
}) => {
  const chartRefs = useRef<{ [key: string]: ChartJS | null }>({});
  const assistant_message_id = useMessage(
    (m) => m.metadata.custom.assistant_message_id
  );
  const messageId = useMessage((m) => m.id);
  const thread_id = useThreadListItem((t) => t.remoteId);

  if (!isOpen) return null;

  const handleDownload = debounce(async (type: FILE_TYPE.POWERPOINT) => {
    try {
      const isExistingThreadId = isValidUUID(thread_id);
      const isExistingMessageId = isValidUUID(
        assistant_message_id || messageId
      );
      if (!isExistingMessageId || !isExistingThreadId) return;

      const images = Object.values(chartRefs.current)
        .filter((chart): chart is ChartJS => chart !== null)
        .map((chart) => chart.toBase64Image());

      let imageIndex = 0;
      const updatedContent = artifact?.text?.replace(
        /```chart([\s\S]*?)```/g,
        () => {
          if (imageIndex < images.length) {
            const image = images[imageIndex++];
            return `![Chart image](${image})`;
          }
          return "[Chart not available]";
        }
      );

      const response = await api.post(
        `/chat/threads/${thread_id}/message/${
          assistant_message_id || messageId
        }/artifact`,
        { content: updatedContent },
        {
          responseType: "blob",
        }
      );

      const mimeMap: Record<string, string> = {
        [FILE_TYPE.POWERPOINT]:
          "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      };

      const fileNameMap: Record<string, string> = {
        [FILE_TYPE.POWERPOINT]: "summary.pptx",
      };

      const blob = new Blob([response.data], { type: mimeMap[type] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileNameMap[type];
      link.target = "_blank";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download file.");
      console.error(error);
    }
  }, 500);

  const components = {
    code: ({ node, inline, className, children, ...props }: any) => {
      const isChart = /language-chart/.test(className || "");
      if (isChart) {
        try {
          const chartKey = `chart-${node.position.start.line}-${node.position.start.column}`;
          const rawConfig = JSON.parse(children);
          const enhancedConfig = applyChartDefaults(rawConfig);
          return loadChart(enhancedConfig, chartRefs, chartKey);
        } catch (e) {
          return <pre>Error parsing chart JSON: {(e as Error).message}</pre>;
        }
      }

      return (
        <pre>
          <code className={className} {...props}>
            {children}
          </code>
        </pre>
      );
    },
  };

  return (
    <div className="fixed inset-y-0 right-0 z-50 w-1/3 transform overflow-hidden border-l border-border bg-background shadow-lg transition-transform duration-300 ease-in-out">
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border p-4">
          <div className="flex flex-col">
            <h2 className="text-lg font-semibold text-foreground">
              {title || "Artifact"}
            </h2>
            <p className="text-sm text-muted-foreground">Role: {role}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="chat-scrollbar flex-1 overflow-auto p-4">
          <div className="prose prose-sm max-w-none text-foreground [&_pre]:bg-muted">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={components}
            >
              {artifact?.text}
            </ReactMarkdown>
          </div>
        </div>

        {/* Footer */}
        {artifact?.args?.kind && (
          <div className="bg-muted/30 border-t border-border p-4">
            <p className="text-xs text-muted-foreground">
              <Button
                onClick={() => handleDownload(FILE_TYPE.POWERPOINT)}
                size="sm"
                className={"w-full hover:bg-[#10a37f]/80"}
              >
                <Download className="mr-2 h-4 w-4" />
                Download_file_summary.pptx
              </Button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
